import { useEffect, useState, useSyncExternalStore, type FormEvent } from 'react';

import { ApiError, call, type Hold, type Item } from '../api';
import { Back, BottomBar, Failed, Picture, ShopIcon, Spinner, StockCheck, Top, useCall, useSignedIn } from '../components/parts';
import { addToPhone, bumpList, onListChange, readList } from '../phone-list';
import { Link, navigate } from '../router';
import { GRADER_COLORS, LOGO_GRADERS, holdError, holdOnCard } from '../words';

// The card page a sticker's QR code opens (José's pick, 22 Oct 2026): the card, its price, its shop and table, a hold
// request (23 Oct 2026: "Hey, please hold this for me. I'm coming right now.") and Add to my list.

const MINUTES = [15, 30, 60];

export function CardPage({ id }: { id: string }) {
  const { data, error, loading, reload } = useCall<Item>(`/api/find/items/${id}`);

  // While the shop has not answered, the page asks again every 30 seconds.
  const waiting = data?.account?.hold?.state === 'asked' && data.account.hold.open;
  useEffect(() => {
    if (!waiting) return;
    const t = setInterval(reload, 30_000);
    return () => clearInterval(t);
  }, [waiting, reload]);

  if (error && !data)
    return (
      <>
        <Top />
        <main>
          <Back href="/shows" label="All shows" />
          {error instanceof ApiError && error.status === 404 ? (
            <p className="empty">This card is no longer for sale.</p>
          ) : (
            <Failed error={error} retry={reload} />
          )}
        </main>
      </>
    );
  if (!data)
    return (
      <>
        <Top />
        <main>
          <Spinner />
        </main>
      </>
    );

  return (
    <>
      <Top />
      <main>
        {data.back ? <Back href={`/s/${encodeURIComponent(data.back.slug)}`} label={data.back.name} /> : <Back href="/shows" label="All shows" />}
        <div className="pic">
          <CardPicture item={data} />
        </div>
        <div className="meta">
          {data.grader ? null : <GradeTag item={data} />}
          <h1>{data.name}</h1>
          {data.set ? <p className="set">{data.set}</p> : null}
          <p className="price">{data.price ?? 'Ask the seller'}</p>
          <StockCheck availability={data.availability} checkedAt={data.checked_at} refresh={reload} loading={loading} error={error} />
        </div>
        <div className="shop">
          <ShopIcon />
          <div>
            <b>{data.shop}</b>
            {data.show ? <span>{data.show}</span> : null}
            {data.plan ? (
              <Link className="find" href={`/s/${encodeURIComponent(data.plan.slug)}?${new URLSearchParams({ table: data.plan.table })}#plan`}>
                Find table {data.plan.table} on the floor plan
              </Link>
            ) : null}
          </div>
        </div>
        <HoldBox item={data} reload={reload} />
      </main>
      <Dock item={data} reload={reload} />
    </>
  );
}

/** A slab of a grader whose logo the site has shows as its slab: the logo, the cert and the grade on its label. */
function CardPicture({ item }: { item: Item }) {
  const picture = <Picture src={item.picture} alt={item.name} className="card-img" />;
  if (!item.grader || !item.grade_value || !LOGO_GRADERS.includes(item.grader)) return picture;
  const said = `${item.grader} ${item.grade_value} slab${item.cert ? `, cert ${item.cert}` : ''}`;
  return (
    <div className="slab" role="group" aria-label={said}>
      <div className="slab-top" style={{ borderBottomColor: GRADER_COLORS[item.grader] ?? '#15171A' }}>
        <span className="sl">
          <img src={`/graders/${item.grader.toLowerCase()}.png`} alt={item.grader} />
          {item.cert ? <small>Cert {item.cert}</small> : null}
        </span>
        <b>{item.grade_value}</b>
      </div>
      <div className="pic-in">{picture}</div>
    </div>
  );
}

function GradeTag({ item }: { item: Item }) {
  if (!item.grade) return null;
  if (item.kind === 'graded' && item.grader_name) {
    const grade = item.grade.replace(new RegExp(`^${item.grader_name}\\s*`), '');
    return (
      <p className="grade">
        <b style={{ background: GRADER_COLORS[item.grader_name.toUpperCase()] ?? '#15171A' }}>{item.grader_name}</b>
        {grade}
      </p>
    );
  }
  return <p className="grade">{item.grade}</p>;
}

function HoldBox({ item, reload }: { item: Item; reload: () => void }) {
  const signedIn = useSignedIn();
  if (item.availability === 'sold') return <div className="hold"><p className="hold-h">This card is sold.</p></div>;
  if (item.availability === 'held' && !(signedIn && item.account?.hold?.open)) return <div className="hold"><p className="hold-h">The shop is holding this card.</p><p className="hold-t">Refresh to check again. Adding to My list does not reserve it.</p></div>;
  if (!signedIn || !item.account)
    return (
      <div className="hold">
        <p className="hold-h">Coming to the table?</p>
        <p className="hold-t">
          <Link href={`/account?back=${encodeURIComponent(`/i/${item.id}`)}`}>Sign in</Link> to ask the shop to hold this card for you.
        </p>
      </div>
    );
  const said = holdOnCard(item.account.hold);
  if ('title' in said)
    return (
      <div className="hold" role="status">
        <p className="hold-h">{said.title}</p>
        <p className="hold-t">{said.text}</p>
        <CancelHold hold={item.account.hold!} label={said.cancel} done={reload} />
      </div>
    );
  return <AskHold item={item} before={said.ask} done={reload} />;
}

function AskHold({ item, before, done }: { item: Item; before: string | null; done: () => void }) {
  const [minutes, setMinutes] = useState(30);
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setNote(null);
    try {
      await call('/api/find/holds', { method: 'POST', body: { id: item.id, minutes, phone: phone.trim() || undefined } });
      done();
    } catch (err) {
      setNote(holdError(err instanceof ApiError ? err.code : 'failed'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="hold" onSubmit={submit}>
      {before || note ? (
        <p className="hold-t" role="status">
          {note ?? before}
        </p>
      ) : null}
      <p className="hold-h">Ask the shop to hold it</p>
      <fieldset className="mins">
        <legend>For how long?</legend>
        {MINUTES.map((m) => (
          <label key={m}>
            <input type="radio" name="minutes" value={m} checked={minutes === m} onChange={() => setMinutes(m)} />
            {m} min
          </label>
        ))}
      </fieldset>
      <label className="ph">
        Your WhatsApp number, if you want a message
        <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" inputMode="tel" placeholder="+60 12 345 6789" maxLength={30} />
      </label>
      <p className="hold-t">Only the shop sees your number, and only while the hold is open.</p>
      <button className="hold-go" type="submit" disabled={busy}>
        {busy ? 'Asking...' : 'Ask to hold'}
      </button>
    </form>
  );
}

export function CancelHold({ hold, label, done, className = 'hold-x' }: { hold: Hold; label: string; done: () => void; className?: string }) {
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    setBusy(true);
    try {
      await call(`/api/find/holds/${hold.id}/cancel`, { method: 'POST' });
    } catch {
      // Already closed: the page shows its answer.
    } finally {
      setBusy(false);
      done();
    }
  };
  return (
    <button className={className} type="button" onClick={cancel} disabled={busy}>
      {label}
    </button>
  );
}

/** Add to my list: to the account when signed in, else to this phone. Once on the list, it opens My list. */
function Dock({ item, reload }: { item: Item; reload: () => void }) {
  const signedIn = useSignedIn();
  const onPhone = useSyncExternalStore(onListChange, () => readList().some((x) => x.id === item.id), () => false);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const on = signedIn ? !!item.account?.on_list : onPhone;

  const add = async () => {
    if (on) return navigate('/list');
    if (!signedIn) {
      setNote(addToPhone(item) ? 'Added. Your list stays on this phone.' : 'This browser cannot keep a list. Open the page in Safari or Chrome.');
      return;
    }
    setBusy(true);
    try {
      const a = await call<{ status: string; max: number }>('/api/find/list', { method: 'POST', body: { id: item.id } });
      setNote(
        a.status === 'added'
          ? 'Added. Your list is saved to your account.'
          : a.status === 'full'
            ? `Your list is full (${a.max} cards). Remove some first.`
            : 'This card is no longer for sale.',
      );
      bumpList();
      reload();
    } catch (err) {
      setNote(err instanceof ApiError && err.status === 0 ? 'No signal. Try again in a moment.' : 'That did not work. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <BottomBar>
      <div className="dock">
        <button className={on ? 'btn on' : 'btn'} type="button" onClick={add} disabled={busy || (!on && item.availability === 'sold')}>
          {on ? 'On my list. Open it' : item.availability === 'sold' ? 'Sold' : 'Add to my list'}
        </button>
        <p className="note" role="status">
          {note ?? (signedIn ? 'Your list is saved to your account.' : 'Your list stays on this phone.')}
        </p>
      </div>
    </BottomBar>
  );
}
